import React, { useState, useEffect, useRef, useContext } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import UnifiedMatchContext from '../../context/matches/UnifiedMatchContext';
import AuthContext from '../../context/auth/authContext';
import './MatchCounterTicker.css';

const MatchCounterTicker = () => {
  const [displayTcgCount, setDisplayTcgCount] = useState(0);
  const [displayVgcCount, setDisplayVgcCount] = useState(0);
  const [displayUserTcgCount, setDisplayUserTcgCount] = useState(0);
  const [displayUserVgcCount, setDisplayUserVgcCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  
  // Get user context
  const { isAuthenticated } = useContext(AuthContext);
  const { tcgMatches, vgcMatches } = useContext(UnifiedMatchContext);

  // Animated counter function
  const animateCount = (start, end, setter, duration = 1000) => {
    const startTime = Date.now();
    const distance = end - start;
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const currentCount = Math.floor(start + distance * easeOutCubic(progress));
      
      setter(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setter(end);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // Fetch match counts with caching
  const fetchMatchCounts = async (showToast = false, forceRefresh = false) => {
    try {
      setError(null);
      
      // Check localStorage cache first
      const cacheKey = 'globalMatchCounts';
      const cacheExpiry = 15 * 60 * 1000; // 15 minutes in milliseconds
      const cachedData = localStorage.getItem(cacheKey);
      const now = Date.now();
      
      let newTcgCount, newVgcCount;
      
      if (!forceRefresh && cachedData) {
        const { tcg, vgc, timestamp } = JSON.parse(cachedData);
        
        // Check if cache is still valid (within 15 minutes)
        if (now - timestamp < cacheExpiry) {
          // Use cached data
          newTcgCount = tcg || 0;
          newVgcCount = vgc || 0;
        } else {
          // Cache expired, fetch new data
          const response = await api.get('/api/stats/global-counts');
          newTcgCount = response.data.tcg || 0;
          newVgcCount = response.data.vgc || 0;
          
          // Update cache
          localStorage.setItem(cacheKey, JSON.stringify({
            tcg: newTcgCount,
            vgc: newVgcCount,
            timestamp: now
          }));
        }
      } else {
        // No cache or force refresh, fetch new data
        const response = await api.get('/api/stats/global-counts');
        newTcgCount = response.data.tcg || 0;
        newVgcCount = response.data.vgc || 0;
        
        // Store in cache
        localStorage.setItem(cacheKey, JSON.stringify({
          tcg: newTcgCount,
          vgc: newVgcCount,
          timestamp: now
        }));
      }

      // Animate the count changes
      animateCount(displayTcgCount, newTcgCount, setDisplayTcgCount);
      animateCount(displayVgcCount, newVgcCount, setDisplayVgcCount);
      
      // Update user counts if authenticated
      if (isAuthenticated) {
        // Check user counts cache
        const userCacheKey = 'userMatchCounts';
        const userCachedData = localStorage.getItem(userCacheKey);
        
        let userTcgCount, userVgcCount;
        
        if (!forceRefresh && userCachedData && tcgMatches && vgcMatches) {
          const { tcg: cachedUserTcg, vgc: cachedUserVgc, timestamp: userTimestamp } = JSON.parse(userCachedData);
          
          // Check if cache is still valid
          if (now - userTimestamp < cacheExpiry) {
            // Use cached data
            userTcgCount = cachedUserTcg || 0;
            userVgcCount = cachedUserVgc || 0;
          } else {
            // Cache expired, use context data
            userTcgCount = Array.isArray(tcgMatches) ? tcgMatches.length : 0;
            userVgcCount = Array.isArray(vgcMatches) ? vgcMatches.length : 0;
            
            // Update cache
            localStorage.setItem(userCacheKey, JSON.stringify({
              tcg: userTcgCount,
              vgc: userVgcCount,
              timestamp: now
            }));
          }
        } else if (tcgMatches && vgcMatches) {
          // No cache or force refresh, use context data
          userTcgCount = Array.isArray(tcgMatches) ? tcgMatches.length : 0;
          userVgcCount = Array.isArray(vgcMatches) ? vgcMatches.length : 0;
          
          // Store in cache
          localStorage.setItem(userCacheKey, JSON.stringify({
            tcg: userTcgCount,
            vgc: userVgcCount,
            timestamp: now
          }));
        } else {
          // No matches loaded yet, try to get from cache
          if (userCachedData) {
            const { tcg: cachedUserTcg, vgc: cachedUserVgc } = JSON.parse(userCachedData);
            userTcgCount = cachedUserTcg || 0;
            userVgcCount = cachedUserVgc || 0;
          } else {
            userTcgCount = 0;
            userVgcCount = 0;
          }
        }
        
        animateCount(displayUserTcgCount, userTcgCount, setDisplayUserTcgCount);
        animateCount(displayUserVgcCount, userVgcCount, setDisplayUserVgcCount);
      }
      
      setLoading(false);
      
      if (showToast) {
        toast.success('Match counts updated!', {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true
        });
      }
    } catch (err) {
      console.error('Error fetching match counts:', err);
      setError('Unable to fetch match counts');
      setLoading(false);
      
      if (showToast) {
        toast.error('Failed to update match counts', {
          position: "bottom-right",
          autoClose: 3000
        });
      }
    }
  };

  // Initial load and set up refresh interval
  useEffect(() => {
    fetchMatchCounts();

    // Refresh every 30 minutes (30 * 60 * 1000 = 1800000 ms)
    // The fetchMatchCounts function will still respect the 15-minute cache
    intervalRef.current = setInterval(() => fetchMatchCounts(false, false), 1800000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update user counts when matches change (and update cache)
  useEffect(() => {
    if (isAuthenticated && tcgMatches && vgcMatches) {
      const userTcgCount = Array.isArray(tcgMatches) ? tcgMatches.length : 0;
      const userVgcCount = Array.isArray(vgcMatches) ? vgcMatches.length : 0;
      
      // Update the cache whenever matches change
      const userCacheKey = 'userMatchCounts';
      localStorage.setItem(userCacheKey, JSON.stringify({
        tcg: userTcgCount,
        vgc: userVgcCount,
        timestamp: Date.now()
      }));
      
      animateCount(displayUserTcgCount, userTcgCount, setDisplayUserTcgCount);
      animateCount(displayUserVgcCount, userVgcCount, setDisplayUserVgcCount);
    }
  }, [tcgMatches, vgcMatches, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading state
  if (loading) {
    return (
      <div className="match-counter-ticker compact">
        <div className="compact-loading">
          <i className="fas fa-circle-notch fa-spin"></i>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return null; // Don't show error state in compact mode
  }

  // Main render - compact version
  return (
    <div className="match-counter-ticker compact">
      <div className="compact-header">
        <i className="fas fa-globe"></i>
        <span className="compact-title">Global Logging Stats</span>
        <button 
          className="refresh-button"
          onClick={() => fetchMatchCounts(true, true)}
          title="Refresh stats"
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>
      
      <div className="compact-counters">
        <div className="compact-counter">
          <span className="compact-label">TCG Matches:</span>
          <span className="compact-value">{displayTcgCount.toLocaleString()}</span>
        </div>
        <div className="compact-divider">|</div>
        <div className="compact-counter">
          <span className="compact-label">VGC Matches:</span>
          <span className="compact-value">{displayVgcCount.toLocaleString()}</span>
        </div>
      </div>
      
      {isAuthenticated && (
        <>
          <div className="compact-user-stats-divider"></div>
          <div className="compact-user-stats">
            <div className="compact-header">
              <i className="fas fa-user"></i>
              <span className="compact-title">Your Stats</span>
            </div>
            <div className="compact-counters">
              <div className="compact-counter">
                <span className="compact-label">TCG:</span>
                <span className="compact-value compact-user-value">{displayUserTcgCount.toLocaleString()}</span>
              </div>
              <div className="compact-divider">|</div>
              <div className="compact-counter">
                <span className="compact-label">VGC:</span>
                <span className="compact-value compact-user-value">{displayUserVgcCount.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MatchCounterTicker;